import { createFileRoute } from "@tanstack/react-router";
import { site, pageUrl } from "@/data/site";
import { Breadcrumbs, PageHeader, Section } from "@/components/site/section";

export const Route = createFileRoute("/accessibility")({
  head: () => ({
    meta: [
      { title: "Accessibility | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "How this site is built for keyboard, screen reader and reduced-motion use, and how to tell Paul about anything that is hard to use.",
      },
      { property: "og:title", content: "Accessibility | Paul's Pens n Things" },
      { property: "og:description", content: "Our commitment to a site everyone can use, and how to report a barrier." },
      { property: "og:url", content: pageUrl("/accessibility") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/accessibility") }],
  }),
  component: Accessibility,
});

function Accessibility() {
  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Accessibility" }]} />
      <PageHeader
        eyebrow="Accessibility"
        title="Everyone should be able to use this site."
        lede="If any part of it gets in your way, tell Paul and it gets fixed."
      />
      <Section className="pt-0">
        <div className="shell max-w-prose space-y-6 text-base leading-relaxed text-ink/85">
          <p>
            This site targets WCAG 2.2 Level AA. In practice that means semantic headings and landmarks, visible focus
            styles, keyboard access to every control, descriptive alternative text on photographs, form fields with
            real labels and clear error messages, and colour contrast that holds up in bright light.
          </p>
          <h2 className="font-[family-name:var(--font-display)] text-2xl text-ink">Motion</h2>
          <p>
            Animations are subtle and decorative. If your device is set to reduce motion, they are switched off
            automatically.
          </p>
          <h2 className="font-[family-name:var(--font-display)] text-2xl text-ink">Ordering another way</h2>
          <p>
            You never have to use the online request form. Email{" "}
            <a href={`mailto:${site.email}`} className="underline underline-offset-4">
              {site.email}
            </a>{" "}
            or call{" "}
            <a href={`tel:${site.phoneHref}`} className="underline underline-offset-4">
              {site.phoneDisplay}
            </a>{" "}
            and Paul will take the details with you.
          </p>
          <h2 className="font-[family-name:var(--font-display)] text-2xl text-ink">Reporting a problem</h2>
          <p>
            Tell Paul what page you were on, what you were trying to do and what device or assistive technology you
            use. He will reply and put it right.
          </p>
        </div>
      </Section>
    </>
  );
}
